import { ERROR_TYPES } from 'layers/content/constants'

/**
 * Stable lookup key for a row in `curriculumCourseManager.courses` (Redux state).
 * Mirrors curriculumContentManager but keyed on courseCode rather than routePath.
 */
export function createCurriculumCourseStateKey(
  contentGroupCode,
  courseCode,
  siteId,
) {
  return `${contentGroupCode}::${courseCode}::${siteId}`
}

const emptySelection = {
  data: null,
  tree: null,
  error: null,
  loading: false,
}

/**
 * Selects a single course tree entry from the curriculumCourseManager slice.
 * Returns the full response data plus surfaced `tree`, plus loading/error.
 */
const selectCurriculumCourseTree = (
  contentGroupCode,
  courseCode,
  siteId,
) => state => {
  try {
    const courseStateKey = createCurriculumCourseStateKey(
      contentGroupCode,
      courseCode,
      siteId,
    )
    const manager = state.curriculumCourseManager
    const entry = manager?.courses?.[courseStateKey]
    const loading = manager?.isFetching

    if (!entry || !entry.data) {
      return {
        ...emptySelection,
        error: entry?.error
          ? {
              message: entry.error,
              errorType: ERROR_TYPES.courseManagerError,
            }
          : null,
        loading,
      }
    }

    const { data } = entry
    return {
      data,
      tree: data.tree || null,
      error: null,
      loading: false,
    }
  } catch (cause) {
    return {
      ...emptySelection,
      error: {
        message: cause?.message || 'Curriculum course selection failed',
        errorType: ERROR_TYPES.curriculumCourseSelectorError,
      },
      loading: false,
    }
  }
}

/**
 * Selects multiple course tree entries at once for a single
 * (contentGroupCode, siteId) pair across an array of courseCodes.
 *
 * Returns an aggregate matching the legacy useCourseManager return shape so
 * downstream consumers can treat it as a drop-in replacement:
 *   { courseTrees, loading, error, fetched }
 *
 * NOTE: `courseTrees` is keyed by courseCode (matches legacy course-name keys
 * like 'K', '1', 'highschool', '6-8_curriculum_tier2', etc.).
 */
const selectCurriculumCourseTrees = (
  contentGroupCode,
  courseCodes,
  siteId,
) => state => {
  const manager = state.curriculumCourseManager
  const courseTrees = {}
  let firstError = null
  let anyLoading = false
  let allFetched = courseCodes.length > 0

  for (const courseCode of courseCodes) {
    const courseStateKey = createCurriculumCourseStateKey(
      contentGroupCode,
      courseCode,
      siteId,
    )
    const entry = manager?.courses?.[courseStateKey]
    if (entry?.data) {
      courseTrees[courseCode] = entry.data
    } else {
      allFetched = false
    }
    if (entry?.error && !firstError) {
      firstError = {
        message: entry.error,
        errorType: ERROR_TYPES.courseManagerError,
      }
    }
  }

  // Slice-wide isFetching mirrors legacy courseManager.isFetching semantics.
  if (manager?.isFetching) {
    anyLoading = true
  }

  return {
    courseTrees,
    loading: anyLoading,
    error: firstError,
    fetched: allFetched,
  }
}

export default {
  selectCurriculumCourseTree,
  selectCurriculumCourseTrees,
}
